import { apiFunction } from '../../Helpers/apiHelper'
import type { BookingDetail } from '../../Types/BookingTypes'

interface ApiResponse<T> {
  data: T
}

/**
 * 予約詳細を取得
 * 
 * @param id 予約ID
 * @returns 予約詳細データ
 * @throws Error API呼び出し失敗時
 */
export const getBookingDetail = async (id: string | number): Promise<BookingDetail> => {
  try {
    const result = await apiFunction<ApiResponse<BookingDetail>>(
      `/api/bookings/${id}`,
      {},
      'GET'
    )
    
    if (!result || !result.data) {
      throw new Error('予約データの形式が正しくありません。')
    }

    return result.data
  } catch (error: any) {
    if (error.statusCode === 404) {
      throw new Error('予約データが見つかりません。')
    }
    if (error.message) {
      throw new Error(error.message)
    }
    throw new Error('予約データの取得に失敗しました。')
  }
}

/**
 * 予約希望日を承認する
 * 
 * @param id 予約ID
 * @param preferredSlotId 希望日ID
 * @param requiresAttendee 立合いが必要かどうか
 * @returns 成功メッセージ
 * @throws Error API呼び出し失敗時
 */
export const approvePreferredSlot = async (
  id: string | number,
  preferredSlotId: number,
  requiresAttendee: boolean
): Promise<void> => {
  try {
    const result = await apiFunction<{ message: string }>(
      `/api/bookings/${id}/approve-slot`,
      {
        preferred_slot_id: preferredSlotId,
        requires_attendee: requiresAttendee
      },
      'POST'
    )
    
    if (!result) {
      throw new Error('承認処理に失敗しました。')
    }
  } catch (error: any) {
    if (error.statusCode === 400) {
      throw new Error(error.message || '承認できませんでした。')
    }
    if (error.statusCode === 404) {
      throw new Error('予約データが見つかりません。')
    }
    if (error.message) {
      throw new Error(error.message)
    }
    throw new Error('承認処理に失敗しました。')
  }
}

/**
 * 予約ステータスを変更する
 * 
 * @param id 予約ID
 * @param status 新しいステータス
 * @returns 成功メッセージ
 * @throws Error API呼び出し失敗時
 */
export const changeBookingStatus = async (
  id: string | number,
  status: string
): Promise<void> => {
  try {
    const result = await apiFunction<{ message: string }>(
      `/api/bookings/${id}/status`,
      {
        status: status
      },
      'PATCH'
    )
    
    if (!result) {
      throw new Error('ステータスの変更に失敗しました。')
    }
  } catch (error: any) {
    if (error.statusCode === 400) {
      throw new Error(error.message || 'ステータスを変更できませんでした。')
    }
    if (error.statusCode === 404) {
      throw new Error('予約データが見つかりません。')
    }
    if (error.message) {
      throw new Error(error.message)
    }
    throw new Error('ステータスの変更に失敗しました。')
  }
}

/**
 * 予約内容を更新する
 * 
 * @param id 予約ID
 * @param data 更新データ
 * @returns 更新後の予約詳細データ
 * @throws Error API呼び出し失敗時
 */
export const updateBooking = async (
  id: string | number,
  data: any
): Promise<BookingDetail> => {
  try {
    const result = await apiFunction<ApiResponse<BookingDetail>>(
      `/api/bookings/${id}`,
      data,
      'PUT'
    )
    
    if (!result || !result.data) {
      throw new Error('予約データの更新に失敗しました。')
    }

    return result.data
  } catch (error: any) {
    if (error.statusCode === 400) {
      throw new Error(error.message || '予約内容を更新できませんでした。')
    }
    if (error.statusCode === 404) {
      throw new Error('予約データが見つかりません。')
    }
    if (error.message) {
      throw new Error(error.message)
    }
    throw new Error('予約内容の更新に失敗しました。')
  }
}

// 型定義を再エクスポート（後方互換性のため）
export type {
  PreferredSlot,
  Contractor,
  ServiceAddress,
  CustomerNumber,
  Attendee,
  Consumer,
  BroughtAppliance,
  BookingDetail
} from '../../Types/BookingTypes'

